import { createContext, useContext, useRef, useState } from "react";
import { toast } from "sonner";

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  // Sept 6 2026 · Karine ask — "Cart Ready Nudge". Fires a subtle
  // Sonner toast the first time an item lands in this session so no
  // client wonders where the cart went again. Guarded by a ref so it
  // never fires twice, and skipped for quick-addon adds inside the
  // drawer (client is already looking at the cart).
  const nudgeShownRef = useRef(false);

  const addToCart = (service) => {
    setCartItems((prev) => {
      // Sept 6 2026 · Karine ask — "add multiple add-ons at one time".
      // Add-ons stack via a `qty` counter instead of blocking duplicates,
      // so a client can book 2× HydraFacial + 3× Laser Genesis in one
      // visit (e.g. bringing family / friends). Regular treatments still
      // dedup — you can only book one Botox BIG 3 per appointment.
      const isStackable = service.source === "quick-addon" || service.source === "combo-card-addon" || service.category === "Add-on";
      const existing = prev.find((item) => item.id === service.id);
      if (existing) {
        if (!isStackable) return prev;              // regular treatments dedup
        return prev.map((item) =>
          item.id === service.id
            ? { ...item, qty: (item.qty || 1) + 1 }
            : item
        );
      }
      // Cart Ready Nudge — first item of the session, and not fired from
      // inside the drawer itself (quick-addons already have visual feedback).
      if (
        !nudgeShownRef.current &&
        prev.length === 0 &&
        service.source !== "quick-addon" &&
        !isCartOpen
      ) {
        nudgeShownRef.current = true;
        setTimeout(() => {
          toast("Your cart is ready · book in 30 seconds", {
            description: "Tap the bag button at the bottom of the page to review + book.",
            duration: 6500,
            action: {
              label: "View cart",
              onClick: () => setIsCartOpen(true),
            },
            className: "kl-cart-nudge",
          });
        }, 150);
      }
      return [...prev, { ...service, qty: 1 }];
    });
  };

  // Increment / decrement quantity of a specific cart line (add-ons only).
  const incrementQty = (serviceId) => {
    setCartItems((prev) =>
      prev.map((item) =>
        item.id === serviceId ? { ...item, qty: (item.qty || 1) + 1 } : item
      )
    );
  };
  const decrementQty = (serviceId) => {
    setCartItems((prev) =>
      prev
        .map((item) =>
          item.id === serviceId ? { ...item, qty: (item.qty || 1) - 1 } : item
        )
        .filter((item) => (item.qty || 0) > 0)
    );
  };

  const removeFromCart = (serviceId) => {
    setCartItems((prev) => prev.filter((item) => item.id !== serviceId));
  };

  const clearCart = () => {
    setCartItems([]);
    setSelectedLocation(null);
  };

  const isInCart = (serviceId) => {
    return cartItems.some((item) => item.id === serviceId);
  };

  const cartCount = cartItems.length;

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        clearCart,
        isInCart,
        cartCount,
        incrementQty,
        decrementQty,
        isCartOpen,
        setIsCartOpen,
        selectedLocation,
        setSelectedLocation,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
